<div class="bk-page gd">
  <header class="bk-page__head">
    <div class="bk-page__intro">
      <app-back-button class="gd__back" label="Back" (back)="goBack()" />
      <p class="bk-eyebrow">Segment</p>
      @if (loading && !segmentDetail) {
        <span class="bk-skel gd__skel-title" aria-hidden="true"></span>
        <span class="bk-skel gd__skel-line" aria-hidden="true"></span>
        <span class="bk-sr-only">Loading the segment</span>
      } @else {
        <h1 class="bk-page__title">{{ loadError ? 'Segment did not load' : name }}</h1>
        @if (!loadError) {
          <p class="bk-page__sub" [class.gd__sub-empty]="!description">{{ description || 'No description' }}</p>
        }
      }
    </div>

    @if (segmentDetail) {
      <div class="bk-page__actions">
        <button
          *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'manage-contact-groups' }"
          type="button"
          class="bk-btn bk-btn--ghost"
          (click)="editConditions()"
        >
          <span class="bk-sym" aria-hidden="true">tune</span>
          Edit conditions
        </button>
        <button
          *hasPermission="{ category: 'MESSAGE_CONTENT_MANAGEMENT', permission: 'compose-sms' }"
          type="button"
          class="bk-btn bk-btn--primary"
          (click)="messageGroup()"
        >
          <span class="bk-sym" aria-hidden="true">send</span>
          Message this segment
        </button>
      </div>
    }
  </header>

  @if (loadError) {
    <section class="bk-panel gd__error" role="alert">
      <h2 class="bk-heading gd__error-title">We could not open this segment</h2>
      <p class="gd__error-text">{{ loadError }}</p>
      <div class="gd__error-actions">
        <button type="button" class="bk-btn bk-btn--primary" (click)="loadSegment()">Load again</button>
        <button type="button" class="bk-btn bk-btn--ghost" (click)="goBack()">Go back</button>
      </div>
    </section>
  } @else if (segmentDetail) {
    <div class="gd__overview">
      <div class="bk-stats gd__stats">
        <div class="bk-stat">
          @if (countError) {
            <div class="bk-stat__value" title="The count did not load. Reload the page to try again." aria-hidden="true">—</div>
            <span class="bk-sr-only">The count did not load</span>
          } @else if (contactsCount === null) {
            <span class="bk-skel gd__stat-skel" aria-hidden="true"></span>
          } @else {
            <div class="bk-stat__value">{{ formatCount(contactsCount) }}</div>
          }
          <div class="bk-stat__label">Contacts match today</div>
        </div>
        @if (reach) {
          <div class="bk-stat">
            <div class="bk-stat__value">{{ formatCount(reach.withNumber) }}</div>
            <div class="bk-stat__label">Have a phone number</div>
          </div>
          <div class="bk-stat" [attr.data-tone]="reach.withoutNumber ? 'bad' : null">
            <div class="bk-stat__value">{{ formatCount(reach.withoutNumber) }}</div>
            <div class="bk-stat__label">No phone number</div>
            @if (reach.withoutNumber) {
              <div class="gd__stat-hint">Messages to this segment skip them.</div>
            }
          </div>
        }
      </div>

      <div class="gd__panels">
        <section class="bk-panel gd__panel" aria-labelledby="seg-rules-title">
          <h2 class="bk-section-title" id="seg-rules-title">Conditions</h2>
          @if (ruleLines.length) {
            @if (ruleSentence) {
              <p class="gd__rule-sentence">Contacts whose {{ ruleSentence }}.</p>
            }
            <ol class="gd__rules">
              @for (line of ruleLines; track $index) {
                <li class="gd__rule" [class.is-legacy]="line.legacy">
                  <span class="gd__rule-join">{{ $first ? 'If' : 'And' }}</span>
                  <span class="gd__rule-text">{{ line.text }}</span>
                  @if (line.legacy) {
                    <span class="gd__rule-flag">Not applied</span>
                  }
                </li>
              }
            </ol>
            @if (hasLegacyRules) {
              <div class="gd__warn" role="note">
                <span class="bk-sym" aria-hidden="true">error</span>
                <div>
                  <p>
                    A condition marked Not applied was saved in a format the server skips, so this segment matches more
                    contacts than it says. Open Edit conditions and save to apply it.
                  </p>
                  <button
                    *hasPermission="{ category: 'CONTACT_MANAGEMENT', permission: 'manage-contact-groups' }"
                    type="button"
                    class="bk-more"
                    (click)="editConditions()"
                  >
                    Edit conditions
                    <span class="bk-sym" aria-hidden="true">arrow_forward</span>
                  </button>
                </div>
              </div>
            }
          } @else {
            <p class="gd__empty">No conditions are saved, so every contact matches.</p>
          }
        </section>

        <section class="bk-panel gd__panel" aria-labelledby="seg-facts-title">
          <h2 class="bk-section-title" id="seg-facts-title">Details</h2>
          <dl class="bk-kv">
            <dt>Created by</dt>
            <dd>{{ createdBy }}</dd>
            <dt>Created</dt>
            <dd>{{ created }}</dd>
            <dt>Updated</dt>
            <dd>{{ updated }}</dd>
          </dl>
        </section>
      </div>
    </div>

    <section class="gd__members" aria-labelledby="seg-members-title">
      <div class="gd__members-head">
        <h2 class="bk-section-title" id="seg-members-title">Matching contacts</h2>
        <p class="gd__members-sub">The list updates as contacts change.</p>
      </div>
      <group-contacts-table
        groupType="segment"
        [groupDetails]="formattedSegmentDetail"
        (updateSelectedContacts)="updateSelectedContacts($event)"
        (action)="handleAction($event)"
        (membersChanged)="onMembersChanged()"
      />
    </section>
  }
</div>

<!-- MODALS -->
<assign-multiple-contacts-to-organizations
  [open]="showAssignMultipleContactsToOrg"
  (close)="closeAssignMultipleContactsToOrg()"
  [selectedContacts]="selectedContacts"
  (resetSelectedContacts)="resetSelectedContacts()"
/>

<assign-multiple-contacts-to-tags
  [open]="showAssignMultipleContactsToTags"
  (close)="closeAssignMultipleContactsToTags()"
  [selectedContacts]="selectedContacts"
  (resetSelectedContacts)="resetSelectedContacts()"
/>

<!-- Deletes contacts everywhere, not just from this segment -->
<app-prompt-card
  [title]="deleteTitle"
  [buttonText]="deleteButton"
  tone="danger"
  impact="This cannot be undone."
  impactIcon="warning"
  [openModal]="showDeleteSelectedContactsPrompt"
  (onClose)="closeDeleteSelectedContactsPrompt()"
  (onButtonClick)="deleteSelectedContacts()"
  [loading]="deleting"
>
  <p class="gd__prompt">
    This deletes {{ selectedContacts.length === 1 ? 'the contact' : 'the ' + selectedContacts.length + ' contacts' }}
    from your whole contact list, with their organizations and tags. To keep them, change the segment's conditions
    instead.
  </p>
</app-prompt-card>
